// ─── src/components/dashboard/CommunityCatalystsTable.tsx ────────────────────

import { useState } from "react";
import { Eye, Trash2, ExternalLink, X, KeyRound, Loader2, Check } from "lucide-react";
import type { CommunityCatalystRegistration } from "../../types/communityCatalyst";
import { setCatalystPassword } from "../../api/communityCatalysts";

interface Props {
  rows: CommunityCatalystRegistration[];
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function CommunityCatalystsTable({ rows, onDelete, isDeleting }: Props) {
  const [selected, setSelected] = useState<CommunityCatalystRegistration | null>(null);
  const [pwFor, setPwFor] = useState<CommunityCatalystRegistration | null>(null);

  if (rows.length === 0) {
    return (
      <div className="card p-10 text-center text-brand-muted">
        No registrations yet.
      </div>
    );
  }

  return (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border">
                <th className="px-4 py-3 text-left text-brand-muted font-medium">#</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Name</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Gender</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Email</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Phone</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Governorate</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Shirt</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Sessions</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Registered</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={r.id}
                  className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors"
                >
                  <td className="px-4 py-3 text-brand-muted">{idx + 1}</td>
                  <td className="px-4 py-3 text-white font-medium whitespace-nowrap" dir="rtl">
                    {r.fullNameAr}
                  </td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap" dir="rtl">
                    {r.gender ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-brand-muted">{r.email}</td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap" dir="ltr">
                    {r.phone}
                  </td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap" dir="rtl">
                    {r.governorate}
                  </td>
                  <td className="px-4 py-3 text-brand-muted uppercase">{r.tShirtSize}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-brand-teal/20 text-brand-tealLight">
                      {r.selectedSessions?.length ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                    {formatDate(r.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSelected(r)}
                        title="View details"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => setPwFor(r)}
                        title="Set dashboard password"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-brand-green hover:bg-brand-green/10 transition-colors"
                      >
                        <KeyRound size={14} />
                      </button>
                      <button
                        onClick={() => onDelete(r.id)}
                        disabled={isDeleting}
                        title="Remove registration"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <DetailModal row={selected} onClose={() => setSelected(null)} />
      )}
      {pwFor && (
        <SetPasswordModal row={pwFor} onClose={() => setPwFor(null)} />
      )}
    </>
  );
}

// ── Set password modal ────────────────────────────────────────────────────────

function SetPasswordModal({
  row, onClose,
}: { row: CommunityCatalystRegistration; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState("");

  const submit = async () => {
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setStatus("saving");
    setError("");
    try {
      await setCatalystPassword(row.email, password);
      setStatus("saved");
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "Couldn't set the password. Please try again.";
      setError(msg);
      setStatus("error");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-white font-semibold text-base">Set dashboard password</p>
            <p className="text-brand-muted text-sm mt-1">{row.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
            <X size={16} />
          </button>
        </div>

        {status === "saved" ? (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-300 text-sm flex items-start gap-2">
            <Check size={16} className="mt-0.5 shrink-0" />
            <span>Password set. Share it with the catalyst separately — it is not stored anywhere in plain text.</span>
          </div>
        ) : (
          <>
            <input
              type="text"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              disabled={status === "saving"}
              className="w-full bg-brand-navy border border-brand-border rounded-lg px-3 py-2 text-white text-sm placeholder-brand-muted focus:outline-none focus:border-brand-green disabled:opacity-60"
            />
            <p className="text-brand-muted text-xs mt-2">
              Shown as text so you can copy it before you close this window. Once
              saved, only the catalyst&rsquo;s bcrypt hash is stored.
            </p>
            {error && <p className="text-red-400 text-xs mt-2">{error}</p>}

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={onClose}
                disabled={status === "saving"}
                className="text-brand-muted hover:text-white text-sm px-3 py-1.5"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={status === "saving"}
                className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-60"
              >
                {status === "saving" ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save password"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Detail modal ───────────────────────────────────────────────────────────────

const SESSION_LABELS: Record<string, string> = {
  "كيف-نكتشف-المؤسسين-قبل-أن-يظهروا؟": "كيف نكتشف المؤسسين قبل أن يظهروا؟",
  "كيف-يصبح-الكتليست-نقطة-وصل-موثوقة؟": "كيف يصبح الكتليست نقطة وصل موثوقة؟",
  "كيف-تجذب-الدعم-إلى-مدينتك؟": "كيف تجذب الدعم إلى مدينتك؟",
  "كيف-نفهم-احتياجات-مدينتنا-بعمق؟": "كيف نفهم احتياجات مدينتنا بعمق؟",
  "كيف-تبني-فعالية-تبقى-في-ذاكرة-الناس؟": "كيف تبني فعالية تبقى في ذاكرة الناس؟",
  "من-فكرة-إلى-مجتمع-كيف-تبدأ-حركة-صغيرة-في-مدينتك؟": "من فكرة إلى مجتمع: كيف تبدأ حركة صغيرة في مدينتك؟",
  "كيف-تبني-شراكات-محلية-بدون-تعقيد؟": "كيف تبني شراكات محلية بدون تعقيد؟",
  "كيف-تحافظ-على-تفاعل-المجتمع-بعد-الفعالية؟": "كيف تحافظ على تفاعل المجتمع بعد الفعالية؟",
  "كيف-نحكي-قصة-مدينتنا؟": "كيف نحكي قصة مدينتنا؟",
  "كيف-نتعامل-مع-التحديات-المحلية-دون-أن-نتوقف؟": "كيف نتعامل مع التحديات المحلية دون أن نتوقف؟",
};

function Field({ label, value, rtl }: { label: string; value?: string | null; rtl?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-brand-muted text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white text-sm whitespace-pre-wrap leading-relaxed" dir={rtl ? "rtl" : "ltr"}>
        {value}
      </p>
    </div>
  );
}

function DetailModal({
  row,
  onClose,
}: {
  row: CommunityCatalystRegistration;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <div dir="rtl">
            <p className="text-white font-bold text-lg">{row.fullNameAr}</p>
            <p className="text-brand-muted text-sm" dir="ltr">{row.email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Gender" value={row.gender} rtl />
          <Field label="Phone" value={row.phone} />
          <Field label="Governorate" value={row.governorate} rtl />
          <Field label="City / Area" value={row.cityArea} rtl />
          <Field label="T-Shirt Size" value={row.tShirtSize.toUpperCase()} />
          <Field label="Available 9–11 Jul" value={row.availableJul9to11} rtl />
          <Field label="Damascus Accommodation" value={row.hasDamascusAccommodation} rtl />
          <Field label="Room Sharing" value={row.roomSharingPreference} rtl />
          <Field label="Dietary" value={row.dietaryPreference} rtl />
          <Field label="Health Needs" value={row.healthNeeds} rtl />

          <div className="sm:col-span-2">
            <Field label="Current Initiative" value={row.currentInitiative} rtl />
          </div>
          <div className="sm:col-span-2">
            <Field label="Bootcamp Goal" value={row.bootcampGoal} rtl />
          </div>

          {/* Sessions */}
          <div className="sm:col-span-2">
            <p className="text-brand-muted text-xs uppercase tracking-wider mb-2">
              Selected Sessions ({row.selectedSessions?.length ?? 0})
            </p>
            <div className="flex flex-wrap gap-2" dir="rtl">
              {(row.selectedSessions ?? []).map((s) => (
                <span
                  key={s}
                  className="px-2.5 py-1 rounded-lg text-xs bg-brand-teal/15 text-brand-tealLight"
                >
                  {SESSION_LABELS[s] ?? s}
                </span>
              ))}
            </div>
          </div>

          {row.notes && (
            <div className="sm:col-span-2">
              <Field label="Notes" value={row.notes} rtl />
            </div>
          )}

          {/* Links */}
          <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2 border-t border-brand-border">
            <LinkPill label="Photo" url={row.photoUrl} />
            <LinkPill label="LinkedIn" url={row.linkedInUrl} />
            <LinkPill label="Social" url={row.socialUrl} />
          </div>
        </div>
      </div>
    </div>
  );
}

function LinkPill({ label, url }: { label: string; url?: string | null }) {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-brand-border text-brand-muted hover:text-white hover:border-brand-green transition-colors"
    >
      <ExternalLink size={12} />
      {label}
    </a>
  );
}
